import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Monitor,
  Smartphone,
  Send,
  Clock,
  Save,
  CheckCircle2,
  ChevronDown,
  X,
  Calendar,
  Twitter,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { SegmentSelector } from "./SegmentSelector";
import { TweetToArticleModal } from "./TweetToArticleModal";
import {
  useSendTestEmail,
  useSendCampaign,
  useSaveDraft,
  useEmailTemplates,
  useSegmentCounts,
} from "@/hooks/use-email";
import { toast } from "sonner";

interface EmailComposerProps {
  onSendTest?: (email: string) => void;
  onSchedule?: (date: Date) => void;
  onSave?: (data: { subject: string; content: string }) => void;
  initialTemplate?: string;
  initialDraftId?: string;
}

// Toolbar button component
function ToolbarButton({
  onClick,
  isActive,
  disabled,
  children,
  tooltip,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  tooltip: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          disabled={disabled}
          className={cn(
            "p-2 rounded-lg transition-all duration-200",
            "hover:bg-muted/80 active:scale-95",
            isActive && "bg-primary/10 text-primary",
            disabled && "opacity-40 cursor-not-allowed"
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function EmailComposer({
  onSendTest,
  onSchedule,
  onSave,
  initialTemplate,
  initialDraftId,
}: EmailComposerProps) {
  const [subject, setSubject] = useState("");
  const [template, setTemplate] = useState(initialTemplate || "daily_brief");
  const [segment, setSegment] = useState<"all" | "pro" | "free">("all");
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showTweetModal, setShowTweetModal] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [draftId, setDraftId] = useState<string | undefined>(initialDraftId);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Hooks for mutations
  const sendTestEmail = useSendTestEmail();
  const sendCampaign = useSendCampaign();
  const saveDraft = useSaveDraft();

  // Fetch templates and segment counts
  const { data: templates } = useEmailTemplates();
  const { data: segmentCounts } = useSegmentCounts();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline underline-offset-4",
        },
      }),
      Placeholder.configure({
        placeholder: "Start writing your newsletter...",
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose-base dark:prose-invert max-w-none focus:outline-none min-h-[400px] px-6 py-4",
      },
    },
  });

  // Auto-save functionality - save every 30 seconds
  const saveContent = useCallback(async () => {
    if (!editor || !subject.trim()) return;

    setIsSaving(true);
    try {
      const result = await saveDraft.mutateAsync({
        id: draftId,
        subject,
        content: editor.getHTML(),
        template,
        segment,
      });

      if (result.id && !draftId) {
        setDraftId(result.id);
      }

      setLastSaved(new Date());
      onSave?.({ subject, content: editor.getHTML() });
    } catch (error) {
      console.error("Failed to save draft:", error);
    } finally {
      setIsSaving(false);
    }
  }, [editor, subject, template, segment, draftId, saveDraft, onSave]);

  // Set up auto-save interval (30 seconds)
  useEffect(() => {
    autoSaveIntervalRef.current = setInterval(() => {
      if (editor && subject.trim() && editor.getHTML().length > 20) {
        saveContent();
      }
    }, 30000);

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [saveContent, editor, subject]);

  // Format time ago
  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  // Update time ago display every 10 seconds
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  // Set link
  const setLink = useCallback(() => {
    if (!editor || !linkUrl) return;

    const url = linkUrl.startsWith("http") ? linkUrl : `https://${linkUrl}`;

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();

    setLinkUrl("");
    setShowLinkInput(false);
  }, [editor, linkUrl]);

  // Remove link
  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
    setShowLinkInput(false);
  }, [editor]);

  // Handle send test
  const handleSendTest = async () => {
    if (!testEmail || !editor) return;

    try {
      await sendTestEmail.mutateAsync({
        to: testEmail,
        subject: subject || "Test Email",
        content: editor.getHTML(),
        template: template as "daily_brief" | "weekly_digest" | "welcome" | "custom",
      });
      toast.success(`Test email sent to ${testEmail}`);
      setTestEmail("");
      onSendTest?.(testEmail);
    } catch (error) {
      toast.error("Failed to send test email");
    }
  };

  // Handle schedule
  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime || !editor) return;

    const scheduledDate = new Date(`${scheduleDate}T${scheduleTime}`);

    try {
      await sendCampaign.mutateAsync({
        subject,
        content: editor.getHTML(),
        template,
        segment,
        scheduleAt: scheduledDate.toISOString(),
      });
      toast.success(`Email scheduled for ${scheduledDate.toLocaleString()}`);
      setShowScheduler(false);
      onSchedule?.(scheduledDate);
    } catch (error) {
      toast.error("Failed to schedule email");
    }
  };

  // Handle send now
  const handleSendNow = async () => {
    if (!editor || !subject.trim()) {
      toast.error("Please enter a subject line");
      return;
    }

    try {
      const result = await sendCampaign.mutateAsync({
        subject,
        content: editor.getHTML(),
        template,
        segment,
      });

      toast.success(
        `Campaign sent to ${result.recipientCount?.toLocaleString() || "all"} subscribers!`
      );
      setShowSendConfirm(false);
    } catch (error) {
      toast.error("Failed to send campaign");
    }
  };

  // Handle insert article from tweet
  const handleInsertArticle = (title: string, content: string) => {
    if (!editor) return;

    // Set subject if empty
    if (!subject.trim()) {
      setSubject(title);
    }

    // Insert content at the end or replace empty content
    const currentContent = editor.getHTML();
    if (currentContent === "<p></p>" || !currentContent) {
      editor.commands.setContent(content);
    } else {
      editor.commands.insertContent("<hr><br>" + content);
    }
  };

  // Load template content when template changes
  useEffect(() => {
    if (templates && template && editor) {
      const selectedTemplate = templates.find((t) => t.category === template);
      if (selectedTemplate && !editor.getHTML().trim()) {
        editor.commands.setContent(selectedTemplate.content);
      }
    }
  }, [template, templates, editor]);

  // Get segment count for display
  const getSegmentCount = () => {
    if (!segmentCounts) return null;
    return segmentCounts[segment as keyof typeof segmentCounts] || 0;
  };

  if (!editor) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-6">
        {/* Top Bar - Subject, Template, Segment */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
              Subject Line
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Your email subject line..."
              className="h-12 text-lg font-medium border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                Template
              </Label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily_brief">Daily Brief</SelectItem>
                  <SelectItem value="deep_dive">Deep Dive</SelectItem>
                  <SelectItem value="breaking_news">Breaking News</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <SegmentSelector
                value={segment}
                onChange={(v) => setSegment(v as "all" | "pro" | "free")}
                counts={segmentCounts}
              />
            </div>
          </div>
        </div>

        {/* Editor Container */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Editor */}
          <Card className="overflow-hidden border-border/50 relative">
            {/* Floating Toolbar */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border/50"
            >
              <div className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center gap-1">
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive("bold")}
                    tooltip="Bold (Cmd+B)"
                  >
                    <Bold className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive("italic")}
                    tooltip="Italic (Cmd+I)"
                  >
                    <Italic className="w-4 h-4" />
                  </ToolbarButton>

                  <Separator orientation="vertical" className="h-6 mx-2" />

                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    isActive={editor.isActive("heading", { level: 1 })}
                    tooltip="Heading 1"
                  >
                    <Heading1 className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    isActive={editor.isActive("heading", { level: 2 })}
                    tooltip="Heading 2"
                  >
                    <Heading2 className="w-4 h-4" />
                  </ToolbarButton>

                  <Separator orientation="vertical" className="h-6 mx-2" />

                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive("bulletList")}
                    tooltip="Bullet List"
                  >
                    <List className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive("orderedList")}
                    tooltip="Numbered List"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive("blockquote")}
                    tooltip="Quote"
                  >
                    <Quote className="w-4 h-4" />
                  </ToolbarButton>

                  <Separator orientation="vertical" className="h-6 mx-2" />

                  <Popover open={showLinkInput} onOpenChange={setShowLinkInput}>
                    <PopoverTrigger asChild>
                      <div>
                        <ToolbarButton
                          onClick={() => {
                            if (editor.isActive("link")) {
                              removeLink();
                            } else {
                              setShowLinkInput(true);
                            }
                          }}
                          isActive={editor.isActive("link")}
                          tooltip={editor.isActive("link") ? "Remove Link" : "Add Link"}
                        >
                          <LinkIcon className="w-4 h-4" />
                        </ToolbarButton>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-3" align="start">
                      <div className="flex gap-2">
                        <Input
                          ref={linkInputRef}
                          value={linkUrl}
                          onChange={(e) => setLinkUrl(e.target.value)}
                          placeholder="https://example.com"
                          className="flex-1"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              setLink();
                            }
                          }}
                        />
                        <Button size="sm" onClick={setLink}>
                          Add
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Separator orientation="vertical" className="h-6 mx-2" />

                  {/* Generate from Tweet Button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowTweetModal(true)}
                        className="gap-1.5 text-sky-500 hover:text-sky-600 hover:bg-sky-500/10"
                      >
                        <Twitter className="w-4 h-4" />
                        <span className="hidden sm:inline">From Tweet</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Generate article from tweet
                    </TooltipContent>
                  </Tooltip>
                </div>

                <div className="flex items-center gap-1">
                  <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    tooltip="Undo"
                  >
                    <Undo className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    tooltip="Redo"
                  >
                    <Redo className="w-4 h-4" />
                  </ToolbarButton>
                </div>
              </div>
            </motion.div>

            {/* Bubble Menu */}
            <BubbleMenu
              editor={editor}
              tippyOptions={{ duration: 100 }}
              className="bg-popover border border-border rounded-lg shadow-lg p-1 flex gap-0.5"
            >
              <button
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={cn(
                  "p-2 rounded hover:bg-muted transition-colors",
                  editor.isActive("bold") && "bg-muted text-primary"
                )}
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={cn(
                  "p-2 rounded hover:bg-muted transition-colors",
                  editor.isActive("italic") && "bg-muted text-primary"
                )}
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  if (editor.isActive("link")) {
                    removeLink();
                  } else {
                    const url = window.prompt("Enter URL:");
                    if (url) {
                      editor.chain().focus().setLink({ href: url }).run();
                    }
                  }
                }}
                className={cn(
                  "p-2 rounded hover:bg-muted transition-colors",
                  editor.isActive("link") && "bg-muted text-primary"
                )}
              >
                <LinkIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className={cn(
                  "p-2 rounded hover:bg-muted transition-colors",
                  editor.isActive("heading", { level: 2 }) && "bg-muted text-primary"
                )}
              >
                <Heading2 className="w-4 h-4" />
              </button>
            </BubbleMenu>

            {/* Editor Content */}
            <div className="min-h-[500px] bg-background">
              <EditorContent editor={editor} />
            </div>

            {/* Auto-save indicator */}
            <div className="absolute bottom-4 right-4">
              <AnimatePresence mode="wait">
                {isSaving ? (
                  <motion.div
                    key="saving"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/80 backdrop-blur-sm px-3 py-1.5 rounded-full"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Saving...
                  </motion.div>
                ) : lastSaved ? (
                  <motion.div
                    key="saved"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/80 backdrop-blur-sm px-3 py-1.5 rounded-full"
                  >
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    Saved {formatTimeAgo(lastSaved)}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </Card>

          {/* Preview Panel */}
          <Card className="overflow-hidden border-border/50 hidden xl:block">
            <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border/50 px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-medium">Preview</span>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setPreviewMode("desktop")}
                  className={cn(
                    "p-1.5 rounded-md transition-all",
                    previewMode === "desktop" && "bg-background shadow-sm"
                  )}
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewMode("mobile")}
                  className={cn(
                    "p-1.5 rounded-md transition-all",
                    previewMode === "mobile" && "bg-background shadow-sm"
                  )}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6 bg-muted/30 min-h-[500px] flex justify-center">
              <motion.div
                layout
                className={cn(
                  "bg-background rounded-lg shadow-xl overflow-hidden transition-all duration-300",
                  previewMode === "desktop" ? "w-full max-w-none" : "w-[375px]"
                )}
              >
                {/* Email Header Preview */}
                <div className="bg-primary/5 border-b border-border px-6 py-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="font-semibold text-primary text-sm">AA</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">Allen Analysis</p>
                      <p className="text-xs text-muted-foreground">newsletter@allenanalysis.com</p>
                    </div>
                  </div>
                  <p className="font-semibold text-foreground">
                    {subject || "Your subject line here..."}
                  </p>
                </div>

                {/* Email Body Preview */}
                <div
                  className="p-6 prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: editor.getHTML() || "<p class='text-muted-foreground'>Start writing to see preview...</p>",
                  }}
                />
              </motion.div>
            </div>
          </Card>
        </div>

        {/* Actions Bar */}
        <Card className="p-4 border-border/50">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            {/* Left: Test Email */}
            <div className="flex items-center gap-2">
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-48"
              />
              <Button
                variant="outline"
                onClick={handleSendTest}
                disabled={!testEmail || sendTestEmail.isPending}
                className="gap-2"
              >
                {sendTestEmail.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send Test
              </Button>
            </div>

            {/* Right: Schedule & Send */}
            <div className="flex items-center gap-2">
              {/* Segment count badge */}
              {segmentCounts && (
                <div className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground px-3">
                  <span>Sending to</span>
                  <span className="font-semibold text-foreground">
                    {getSegmentCount()?.toLocaleString()}
                  </span>
                  <span>subscribers</span>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => saveContent()}
                disabled={isSaving || saveDraft.isPending}
                className="gap-2"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Draft
              </Button>

              <Popover open={showScheduler} onOpenChange={setShowScheduler}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Clock className="w-4 h-4" />
                    Schedule
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" align="end">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Schedule Send</h4>
                      <button
                        onClick={() => setShowScheduler(false)}
                        className="p-1 hover:bg-muted rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs mb-1.5 block">Date</Label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="date"
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs mb-1.5 block">Time</Label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="time"
                            value={scheduleTime}
                            onChange={(e) => setScheduleTime(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                    </div>
                    <Button
                      className="w-full gap-2"
                      onClick={handleSchedule}
                      disabled={!scheduleDate || !scheduleTime || sendCampaign.isPending}
                    >
                      {sendCampaign.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                      Schedule Email
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                className="gap-2 px-6"
                onClick={() => setShowSendConfirm(true)}
                disabled={sendCampaign.isPending}
              >
                {sendCampaign.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send Now
              </Button>
            </div>
          </div>
        </Card>

        {/* Tweet to Article Modal */}
        <TweetToArticleModal
          open={showTweetModal}
          onOpenChange={setShowTweetModal}
          onInsertArticle={handleInsertArticle}
        />

        {/* Send Confirmation Dialog */}
        <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Confirm Send Campaign
              </AlertDialogTitle>
              <AlertDialogDescription>
                You are about to send this email to{" "}
                <strong>{getSegmentCount()?.toLocaleString() || "all"}</strong> subscribers
                in the <strong>{segment}</strong> segment.
                <br /><br />
                Subject: <strong>{subject || "(no subject)"}</strong>
                <br /><br />
                This action cannot be undone. Are you sure you want to proceed?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleSendNow}
                className="bg-primary hover:bg-primary/90"
              >
                {sendCampaign.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Now
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
